import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { formatMexicoDateTime, mexicoDateJsonReplacer } from '../../dateUtils';
import {
  ChevronLeft,
  ChevronRight,
  RefreshCw,
  Search,
  X
} from 'lucide-react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis
} from 'recharts';
import {
  fetchTicketsControlAgentsRealtime,
  fetchTicketsControlAssignmentDetail,
  fetchTicketsControlAssignmentsHistory,
  fetchTicketsControlCargaTecnicos,
  fetchTicketsControlDashboard,
  fetchTicketsControlSinAsignar,
  fetchTicketsControlSlaAssignmentByAgent,
  fetchTicketsControlSlaAssignmentByType,
  fetchTicketsControlSlaAssignmentSummary,
  fetchTicketsControlSlaResumen,
  fetchTicketsControlTicketDetail,
  fetchTicketsControlTickets
} from '../../api';

// KIO branding icons
const KIO = {
  ticket:      '/assets/icons/ServicesDesk.png',
  activity:    '/assets/icons/KIO_Business Chart.png',
  check:       '/assets/icons/KIO_Check.png',
  shield:      '/assets/icons/KIO_Shield.png',
  warning:     '/assets/icons/KIO_Report Problem Warning Triangle.png',
  gauge:       '/assets/icons/KIO_Graphic.png',
  team:        '/assets/icons/KIO_TEAM.png',
  clock:       '/assets/icons/KIO_Clock .png',
  userCheck:   '/assets/icons/KIO_Users People q.png',
  bot:         '/assets/icons/KIO_IA.png',
  brain:       '/assets/icons/KIO_IA1.png',
  server:      '/assets/icons/KIO_Server.png',
  target:      '/assets/icons/KIO_Target.png',
  loop:        '/assets/icons/KIO_Loop Diamond.png',
  time:        '/assets/icons/KIO_Time.png',
};
function KioImg({ src, className = 'h-5 w-5', style }) {
  return <img src={src} alt="" className={className} style={{ objectFit: 'contain', ...style }} />;
}


const DATE_PRESETS = [
  { value: 'last_7_days', label: 'Ultimos 7 dias' },
  { value: 'last_14_days', label: 'Ultimos 14 dias' },
  { value: 'last_30_days', label: 'Ultimos 30 dias' },
  { value: 'last_90_days', label: 'Ultimos 90 dias' },
  { value: 'current_month', label: 'Mes actual' },
  { value: 'previous_month', label: 'Mes anterior' },
  { value: 'custom', label: 'Custom Range' }
];

const TABS = [
  { key: 'tickets', label: 'Tickets', icon: KIO.ticket },
  { key: 'unassigned', label: 'Sin asignar', icon: KIO.shield },
  { key: 'assignments', label: 'Asignaciones', icon: KIO.loop },
  { key: 'agents', label: 'Agentes', icon: KIO.team }
];

const asArray = (value) => {
  if (Array.isArray(value)) return value;
  if (Array.isArray(value?.items)) return value.items;
  if (Array.isArray(value?.data)) return value.data;
  if (Array.isArray(value?.records)) return value.records;
  return [];
};

const getNumber = (value, fallback = 0) => {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
};

const fmtPct = (value) => `${getNumber(value).toFixed(1)}%`;
const fmtMin = (value) => `${getNumber(value).toFixed(1)} min`;

const sectionCard = 'rounded-2xl border border-violet-500/25 bg-[#1a0d36]/70 p-5 backdrop-blur-md';

function Skeleton({ className = '' }) {
  return <div className={`animate-pulse rounded-xl bg-white/10 ${className}`} />;
}

function StatusBadge({ value }) {
  const text = String(value || 'N/D');
  const low = text.toLowerCase();

  let classes = 'border-slate-400/30 bg-slate-500/20 text-slate-200';
  if (low.includes('ok') || low.includes('close') || low.includes('disponible')) {
    classes = 'border-emerald-400/30 bg-emerald-500/20 text-emerald-200';
  } else if (low.includes('riesgo') || low.includes('pend')) {
    classes = 'border-amber-400/30 bg-amber-500/20 text-amber-200';
  } else if (low.includes('venc') || low.includes('overdue')) {
    classes = 'border-red-400/30 bg-red-500/20 text-red-200';
  }

  return <span className={`rounded-full border px-2 py-1 text-xs ${classes}`}>{text}</span>;
}

function KPICard({ title, value, subtitle, icon, accent = 'from-violet-500 to-fuchsia-600' }) {
  return (
    <div className="rounded-2xl border border-violet-400/30 bg-white/8 p-4 backdrop-blur-md transition-all duration-300 hover:-translate-y-0.5 hover:border-violet-300/60 hover:shadow-[0_0_20px_rgba(168,85,247,0.25)]">
      <div className="mb-2 flex items-start justify-between gap-2">
        <p className="text-xs uppercase tracking-wide text-violet-200/70">{title}</p>
        <div className={`flex h-11 w-11 items-center justify-center rounded-xl bg-gradient-to-r ${accent}`}>
          {typeof icon === 'string'
            ? <img src={icon} alt="" className="h-7 w-auto max-w-[2rem]" style={{ objectFit: 'contain', filter: 'brightness(0) invert(1)' }} />
            : icon ? React.createElement(icon, { className: 'h-7 w-auto text-white' }) : null}
        </div>
      </div>
      <p className="text-2xl font-bold">{value}</p>
      {subtitle ? <p className="mt-1 text-xs text-violet-100/70">{subtitle}</p> : null}
    </div>
  );
}

function EmptyState({ title, description }) {
  return (
    <div className="rounded-2xl border border-violet-500/25 bg-[#190a33]/70 p-8 text-center">
      <p className="text-lg font-semibold text-violet-100">{title}</p>
      <p className="mt-2 text-sm text-violet-200/70">{description}</p>
    </div>
  );
}

export default function TicketsControlPage() {
  const allowedOpsTeams = useMemo(() => new Set(['OPS_N2', 'OPS_SAAS']), []);

  const [datePreset, setDatePreset] = useState('current_month');
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');

  const [filters, setFilters] = useState({
    group: '',
    technician: '',
    priority: '',
    sla_state: '',
    only_open: true,
    only_sla_risk: false,
    only_overdue: false
  });

  const [activeTab, setActiveTab] = useState('tickets');
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('ticket_id');
  const [sortOrder, setSortOrder] = useState('desc');
  const [assignmentTypeFilter, setAssignmentTypeFilter] = useState('');

  const [loadingSummary, setLoadingSummary] = useState(true);
  const [loadingTab, setLoadingTab] = useState(true);
  const [errorMessage, setErrorMessage] = useState('');
  const [lastUpdated, setLastUpdated] = useState(null);

  const [dashboard, setDashboard] = useState({});
  const [slaResumen, setSlaResumen] = useState({});
  const [cargaTecnicos, setCargaTecnicos] = useState([]);
  const [slaByType, setSlaByType] = useState([]);
  const [slaByAgent, setSlaByAgent] = useState([]);
  const [slaAssignmentSummary, setSlaAssignmentSummary] = useState({});

  const [ticketsData, setTicketsData] = useState({ items: [], total: 0 });
  const [unassignedData, setUnassignedData] = useState([]);
  const [assignmentsData, setAssignmentsData] = useState({ items: [], total: 0 });
  const [agentsData, setAgentsData] = useState([]);

  const [ticketPage, setTicketPage] = useState(1);
  const [assignmentPage, setAssignmentPage] = useState(1);
  const pageSize = 20;

  const [selectedTicketId, setSelectedTicketId] = useState(null);
  const [ticketDetail, setTicketDetail] = useState(null);
  const [ticketDetailLoading, setTicketDetailLoading] = useState(false);

  const [selectedAssignmentTicketId, setSelectedAssignmentTicketId] = useState(null);
  const [assignmentDetail, setAssignmentDetail] = useState(null);
  const [assignmentDetailLoading, setAssignmentDetailLoading] = useState(false);

  const globalQueryFilters = useMemo(() => {
    const payload = {
      ...filters,
      date_preset: datePreset
    };

    if (datePreset === 'custom') {
      payload.start_date = customStartDate;
      payload.end_date = customEndDate;
    }

    return payload;
  }, [customEndDate, customStartDate, datePreset, filters]);

  const loadSummary = useCallback(async () => {
    setLoadingSummary(true);
    setErrorMessage('');

    try {
      const [
        dashboardRes,
        slaResumenRes,
        cargaRes,
        slaAssignmentSummaryRes,
        slaByTypeRes,
        slaByAgentRes
      ] = await Promise.all([
        fetchTicketsControlDashboard(globalQueryFilters),
        fetchTicketsControlSlaResumen(globalQueryFilters),
        fetchTicketsControlCargaTecnicos(globalQueryFilters),
        fetchTicketsControlSlaAssignmentSummary(globalQueryFilters),
        fetchTicketsControlSlaAssignmentByType(globalQueryFilters),
        fetchTicketsControlSlaAssignmentByAgent(globalQueryFilters)
      ]);

      const dashboardPayload = dashboardRes?.kpis || dashboardRes || {};
      setDashboard(dashboardPayload);
      setSlaResumen(slaResumenRes || {});
      setCargaTecnicos(asArray(cargaRes));
      setSlaAssignmentSummary(slaAssignmentSummaryRes || dashboardRes?.sla_assignment_summary || {});
      setSlaByType(asArray(slaByTypeRes));
      setSlaByAgent(asArray(slaByAgentRes));
      setLastUpdated(new Date());
    } catch (error) {
      console.error(error);
      setErrorMessage('No se pudieron cargar las metricas operativas.');
    } finally {
      setLoadingSummary(false);
    }
  }, [globalQueryFilters]);

  const loadTab = useCallback(async () => {
    setLoadingTab(true);

    try {
      if (activeTab === 'tickets') {
        const payload = await fetchTicketsControlTickets({
          ...globalQueryFilters,
          search: searchTerm,
          sort_by: sortBy,
          sort_order: sortOrder,
          limit: pageSize,
          offset: (ticketPage - 1) * pageSize
        });

        setTicketsData({
          items: asArray(payload),
          total: getNumber(payload?.total ?? payload?.count ?? asArray(payload).length)
        });
      }

      if (activeTab === 'unassigned') {
        const payload = await fetchTicketsControlSinAsignar(globalQueryFilters);
        setUnassignedData(asArray(payload));
      }

      if (activeTab === 'assignments') {
        const payload = await fetchTicketsControlAssignmentsHistory({
          ...globalQueryFilters,
          assignment_type: assignmentTypeFilter,
          limit: pageSize,
          offset: (assignmentPage - 1) * pageSize
        });

        setAssignmentsData({
          items: asArray(payload),
          total: getNumber(payload?.total ?? payload?.count ?? asArray(payload).length)
        });
      }

      if (activeTab === 'agents') {
        const payload = await fetchTicketsControlAgentsRealtime(globalQueryFilters);
        const filteredAgents = asArray(payload).filter((row) => {
          const team = String(row.equipo || row.team_id || row.team || row.grupo || '').trim().toUpperCase();
          return allowedOpsTeams.has(team);
        });
        setAgentsData(filteredAgents);
      }
    } catch (error) {
      console.error(error);
      setErrorMessage('No se pudo cargar la informacion de la pestana activa.');
    } finally {
      setLoadingTab(false);
    }
  }, [activeTab, allowedOpsTeams, assignmentPage, assignmentTypeFilter, globalQueryFilters, pageSize, searchTerm, sortBy, sortOrder, ticketPage]);

  useEffect(() => {
    loadSummary();
  }, [loadSummary]);

  useEffect(() => {
    loadTab();
  }, [loadTab]);

  useEffect(() => {
    const interval = setInterval(() => {
      loadSummary();
      loadTab();
    }, 180000);

    return () => clearInterval(interval);
  }, [loadSummary, loadTab]);

  const heatmapData = useMemo(() => {
    return [
      { name: 'Riesgo', value: getNumber(slaResumen.riesgo ?? slaResumen.sla_riesgo), color: '#f59e0b' },
      { name: 'Vencidos', value: getNumber(slaResumen.vencidos ?? slaResumen.sla_vencido), color: '#ef4444' },
      { name: 'OK', value: getNumber(slaResumen.ok ?? slaResumen.sla_ok), color: '#22c55e' },
      { name: 'Sin datos', value: getNumber(slaResumen.sin_datos), color: '#6b7280' }
    ];
  }, [slaResumen]);

  const comparisonData = useMemo(() => {
    return asArray(slaByType).map((row, idx) => ({
      type: String(row.type || row.assignment_type || row.tipo || `Tipo ${idx + 1}`).toUpperCase(),
      compliance: getNumber(row.sla_compliance ?? row.sla_compliance_pct ?? row.sla_ok_percent),
      avg_assignment: getNumber(row.avg_assignment_minutes),
      overdue: getNumber(row.sla_overdue ?? row.overdue)
    }));
  }, [slaByType]);

  const [topAgents, riskAgents] = useMemo(() => {
    const mapped = asArray(slaByAgent).map((row, idx) => ({
      name: row.tecnico || row.agent || row.name || `Agente ${idx + 1}`,
      compliance: getNumber(row.sla_compliance ?? row.sla_compliance_pct ?? row.sla_ok_percent),
      avgAssignment: getNumber(row.avg_assignment_minutes),
      overdue: getNumber(row.sla_overdue),
      load: getNumber(row.load_percent ?? row.carga_percent ?? row.tickets_total)
    }));

    const best = [...mapped].sort((a, b) => b.compliance - a.compliance).slice(0, 5);
    const risk = [...mapped]
      .sort((a, b) => (b.overdue + b.load) - (a.overdue + a.load))
      .slice(0, 5);

    return [best, risk];
  }, [slaByAgent]);

  const lastUpdatedLabel = useMemo(() => {
    if (!lastUpdated) return 'Sin actualizacion';
    const mins = Math.max(1, Math.floor((Date.now() - lastUpdated.getTime()) / 60000));
    return `Actualizado hace ${mins} min`;
  }, [lastUpdated]);

  const ticketPages = Math.max(1, Math.ceil(getNumber(ticketsData.total) / pageSize));
  const assignmentPages = Math.max(1, Math.ceil(getNumber(assignmentsData.total) / pageSize));

  const kpiTotal = getNumber(dashboard.total_tickets);
  const kpiOpen = getNumber(dashboard.tickets_abiertos ?? dashboard.tickets_open);
  const kpiClosed = getNumber(dashboard.tickets_cerrados ?? dashboard.tickets_closed, Math.max(0, kpiTotal - kpiOpen));
  const kpiRisk = getNumber(dashboard.tickets_riesgo_sla ?? dashboard.sla_risk);
  const kpiOverdue = getNumber(dashboard.tickets_sla_vencido ?? dashboard.sla_overdue);
  const kpiUnassigned = getNumber(dashboard.tickets_sin_asignar ?? dashboard.unassigned_tickets);
  const kpiSlaOkPct = kpiOpen > 0 ? ((Math.max(0, kpiOpen - kpiRisk - kpiOverdue) / kpiOpen) * 100) : 0;

  const handleRefresh = async () => {
    await Promise.all([loadSummary(), loadTab()]);
  };

  const openTicketDrawer = async (ticketId) => {
    if (!ticketId) return;
    setSelectedTicketId(ticketId);
    setTicketDetailLoading(true);

    try {
      const payload = await fetchTicketsControlTicketDetail(ticketId, globalQueryFilters);
      setTicketDetail(payload || null);
    } finally {
      setTicketDetailLoading(false);
    }
  };

  const openAssignmentModal = async (ticketId) => {
    if (!ticketId) return;
    setSelectedAssignmentTicketId(ticketId);
    setAssignmentDetailLoading(true);

    try {
      const payload = await fetchTicketsControlAssignmentDetail(ticketId, globalQueryFilters);
      setAssignmentDetail(payload || null);
    } finally {
      setAssignmentDetailLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[radial-gradient(circle_at_top_right,rgba(124,58,237,0.22),transparent_30%),radial-gradient(circle_at_bottom_left,rgba(34,211,238,0.12),transparent_25%),linear-gradient(135deg,#140728_0%,#0b041a_50%,#1a0b36_100%)] px-4 pb-8 pt-6 text-white md:px-6 xl:px-8">
      <div className="mb-6 rounded-2xl border border-violet-500/35 bg-[#1d0f3d]/75 p-5 backdrop-blur-md">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <h1 className="text-3xl font-bold text-violet-100">Control de Tickets</h1>
            <p className="mt-1 text-sm text-violet-200/80">Visibilidad operativa de tickets, SLA y asignacion automatica</p>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <div className="flex items-center gap-2 rounded-xl border border-violet-400/30 bg-black/20 px-3 py-2">
              <KioImg src={KIO.time} className="h-4 w-4" style={{ filter: 'brightness(0) saturate(100%) invert(80%) sepia(20%) saturate(400%) hue-rotate(220deg)' }} />
              <select value={datePreset} onChange={(e) => setDatePreset(e.target.value)} className="bg-transparent text-sm outline-none">
                {DATE_PRESETS.map((item) => (
                  <option key={item.value} value={item.value} className="bg-[#1b0f38]">{item.label}</option>
                ))}
              </select>
            </div>

            {datePreset === 'custom' && (
              <>
                <input type="date" value={customStartDate} onChange={(e) => setCustomStartDate(e.target.value)} className="rounded-xl border border-violet-400/30 bg-black/20 px-3 py-2 text-sm" />
                <input type="date" value={customEndDate} onChange={(e) => setCustomEndDate(e.target.value)} className="rounded-xl border border-violet-400/30 bg-black/20 px-3 py-2 text-sm" />
              </>
            )}

            <button onClick={handleRefresh} className="inline-flex items-center gap-2 rounded-xl border border-violet-300/40 bg-violet-500/20 px-3 py-2 text-sm font-semibold hover:bg-violet-500/35">
              <RefreshCw className="h-4 w-4" /> Actualizar
            </button>
            <span className="rounded-xl border border-violet-500/30 bg-black/20 px-3 py-2 text-xs text-violet-200/80">{lastUpdatedLabel}</span>
          </div>
        </div>

        <div className="mt-4 grid grid-cols-1 gap-2 md:grid-cols-2 xl:grid-cols-4">
          <input placeholder="Grupo" value={filters.group} onChange={(e) => setFilters((prev) => ({ ...prev, group: e.target.value }))} className="rounded-xl border border-violet-400/20 bg-black/20 px-3 py-2 text-sm" />
          <input placeholder="Tecnico" value={filters.technician} onChange={(e) => setFilters((prev) => ({ ...prev, technician: e.target.value }))} className="rounded-xl border border-violet-400/20 bg-black/20 px-3 py-2 text-sm" />
          <input placeholder="Prioridad" value={filters.priority} onChange={(e) => setFilters((prev) => ({ ...prev, priority: e.target.value }))} className="rounded-xl border border-violet-400/20 bg-black/20 px-3 py-2 text-sm" />
          <input placeholder="SLA" value={filters.sla_state} onChange={(e) => setFilters((prev) => ({ ...prev, sla_state: e.target.value }))} className="rounded-xl border border-violet-400/20 bg-black/20 px-3 py-2 text-sm" />
        </div>

        <div className="mt-3 flex flex-wrap gap-4 text-xs text-violet-200/90">
          <label className="inline-flex items-center gap-2"><input type="checkbox" checked={filters.only_open} onChange={(e) => setFilters((prev) => ({ ...prev, only_open: e.target.checked }))} /> Solo abiertos</label>
          <label className="inline-flex items-center gap-2"><input type="checkbox" checked={filters.only_sla_risk} onChange={(e) => setFilters((prev) => ({ ...prev, only_sla_risk: e.target.checked }))} /> Solo riesgo SLA</label>
          <label className="inline-flex items-center gap-2"><input type="checkbox" checked={filters.only_overdue} onChange={(e) => setFilters((prev) => ({ ...prev, only_overdue: e.target.checked }))} /> Solo vencidos</label>
        </div>
      </div>

      {errorMessage && <div className="mb-6 rounded-xl border border-red-500/35 bg-red-500/10 p-3 text-sm text-red-200">{errorMessage}</div>}

      <section className="mb-6 grid grid-cols-1 gap-3 md:grid-cols-3 xl:grid-cols-9">
        {loadingSummary ? (
          Array.from({ length: 9 }).map((_, i) => <Skeleton key={i} className="h-28" />)
        ) : (
          <>
            <KPICard title="Total tickets" value={kpiTotal} icon={KIO.ticket} />
            <KPICard title="Tickets abiertos" value={kpiOpen} icon={KIO.activity} accent="from-cyan-500 to-blue-600" />
            <KPICard title="Tickets cerrados" value={kpiClosed} icon={KIO.check} accent="from-emerald-500 to-green-600" />
            <KPICard title="Riesgo SLA" value={kpiRisk} icon={KIO.shield} accent="from-amber-500 to-orange-600" />
            <KPICard title="SLA vencido" value={kpiOverdue} icon={KIO.warning} accent="from-rose-500 to-red-600" />
            <KPICard title="SLA OK %" value={fmtPct(kpiSlaOkPct)} icon={KIO.gauge} accent="from-emerald-500 to-teal-600" />
            <KPICard title="Sin asignar" value={kpiUnassigned} icon={KIO.team} accent="from-amber-500 to-red-500" />
            <KPICard title="Avg asignacion" value={fmtMin(slaAssignmentSummary.avg_minutes_to_assign ?? slaAssignmentSummary.avg_assignment_minutes)} icon={KIO.clock} accent="from-indigo-500 to-violet-600" />
            <KPICard title="Asignados %" value={fmtPct(slaAssignmentSummary.pct_assigned_under_15_min ?? dashboard.assigned_percent)} icon={KIO.userCheck} accent="from-sky-500 to-cyan-600" />
          </>
        )}
      </section>

      <section className="mb-6 grid grid-cols-1 gap-5 xl:grid-cols-12">
        <div className={`${sectionCard} xl:col-span-5`}>
          <div className="mb-4 flex items-center gap-2"><KioImg src={KIO.shield} /><h3 className="text-sm font-semibold">Heatmap SLA</h3></div>
          <div className="h-[270px]">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={heatmapData} layout="vertical" margin={{ top: 8, right: 12, left: 12, bottom: 8 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.12)" />
                <XAxis type="number" stroke="#c4b5fd" fontSize={11} />
                <YAxis dataKey="name" type="category" stroke="#c4b5fd" fontSize={11} width={72} />
                <Tooltip contentStyle={{ background: 'rgba(15, 7, 32, 0.95)', border: '1px solid rgba(167, 139, 250, 0.45)', borderRadius: '0.75rem' }} />
                <Bar dataKey="value" radius={[0, 6, 6, 0]}>
                  {heatmapData.map((entry) => <Cell key={entry.name} fill={entry.color} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className={`${sectionCard} xl:col-span-7`}>
          <div className="mb-4 flex items-center gap-2"><KioImg src={KIO.team} /><h3 className="text-sm font-semibold">Carga por tecnico</h3></div>
          <div className="overflow-x-auto">
            <table className="min-w-full text-left text-xs">
              <thead><tr className="border-b border-violet-500/20 text-violet-200/80"><th className="px-2 py-2">Tecnico</th><th className="px-2 py-2">Equipo</th><th className="px-2 py-2">Estado</th><th className="px-2 py-2">Tickets</th><th className="px-2 py-2">Riesgo</th><th className="px-2 py-2">Vencido</th><th className="px-2 py-2">Edad prom</th><th className="px-2 py-2">Load %</th></tr></thead>
              <tbody>
                {cargaTecnicos.slice(0, 10).map((row, idx) => {
                  const load = getNumber(row.load_percentage ?? row.load_percent ?? row.load_pct);
                  return (
                    <tr key={`${row.tecnico || row.technician || idx}`} className="border-b border-violet-500/10">
                      <td className="px-2 py-2">{row.tecnico || row.technician || 'N/D'}</td>
                      <td className="px-2 py-2">{row.equipo || row.team || 'N/D'}</td>
                      <td className="px-2 py-2"><StatusBadge value={row.status_actual || row.estado || row.status} /></td>
                      <td className="px-2 py-2">{getNumber(row.tickets_abiertos ?? row.tickets)}</td>
                      <td className="px-2 py-2">{getNumber(row.tickets_riesgo_sla ?? row.sla_risk)}</td>
                      <td className="px-2 py-2">{getNumber(row.tickets_sla_vencido ?? row.sla_overdue)}</td>
                      <td className="px-2 py-2">{fmtMin((row.edad_promedio_horas ?? row.avg_age_minutes) * 60)}</td>
                      <td className="px-2 py-2"><div className="flex items-center gap-2"><div className="h-2 w-20 rounded-full bg-black/30"><div className={`h-2 rounded-full ${load > 85 ? 'bg-red-500' : load > 65 ? 'bg-amber-400' : 'bg-emerald-500'}`} style={{ width: `${Math.min(100, load)}%` }} /></div><span>{load.toFixed(0)}%</span></div></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      <section className="mb-6 grid grid-cols-1 gap-5 xl:grid-cols-12">
        <div className={`${sectionCard} xl:col-span-5`}>
          <div className="mb-4 flex items-center gap-2"><KioImg src={KIO.server} /><h3 className="text-sm font-semibold">SLA vs Asignacion</h3></div>
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            <KPICard title="Avg asignacion" value={fmtMin(slaAssignmentSummary.avg_minutes_to_assign ?? slaAssignmentSummary.avg_assignment_minutes)} icon={KIO.clock} accent="from-indigo-500 to-violet-600" />
            <KPICard title="SLA AUTO %" value={fmtPct(slaAssignmentSummary.sla_met_auto_pct ?? slaAssignmentSummary.sla_auto_percent)} icon={KIO.bot} accent="from-cyan-500 to-blue-600" />
            <KPICard title="SLA MANUAL %" value={fmtPct(slaAssignmentSummary.sla_met_manual_pct ?? slaAssignmentSummary.sla_manual_percent)} icon={KIO.userCheck} accent="from-emerald-500 to-teal-600" />
            <KPICard title="Sin asignar" value={getNumber(slaAssignmentSummary.tickets_without_assignment ?? slaAssignmentSummary.unassigned_tickets)} icon={KIO.team} accent="from-amber-500 to-orange-600" />
            <KPICard title="Riesgo sin asignar" value={getNumber(slaAssignmentSummary.tickets_at_risk_unassigned ?? slaAssignmentSummary.unassigned_risk)} icon={KIO.warning} accent="from-rose-500 to-red-600" />
          </div>
        </div>

        <div className={`${sectionCard} xl:col-span-7`}>
          <div className="mb-4 flex items-center gap-2"><KioImg src={KIO.brain} /><h3 className="text-sm font-semibold">Comparativo AUTO vs MANUAL vs OVERRIDE</h3></div>
          {!comparisonData.length ? (
            <EmptyState title="Sin datos comparativos" description="No hay informacion para el rango seleccionado." />
          ) : (
            <div className="h-[290px]"><ResponsiveContainer width="100%" height="100%"><BarChart data={comparisonData}><CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.15)" /><XAxis dataKey="type" stroke="#c4b5fd" fontSize={11} /><YAxis stroke="#c4b5fd" fontSize={11} /><Tooltip contentStyle={{ background: 'rgba(15, 7, 32, 0.95)', border: '1px solid rgba(167, 139, 250, 0.45)', borderRadius: '0.75rem' }} /><Legend /><Bar dataKey="compliance" name="SLA Compliance" fill="#22c55e" radius={[4, 4, 0, 0]} /><Bar dataKey="avg_assignment" name="Avg Assignment" fill="#38bdf8" radius={[4, 4, 0, 0]} /><Bar dataKey="overdue" name="SLA Overdue" fill="#f43f5e" radius={[4, 4, 0, 0]} /></BarChart></ResponsiveContainer></div>
          )}
        </div>
      </section>

      <section className="mb-6 grid grid-cols-1 gap-5 xl:grid-cols-2">
        <div className={sectionCard}><div className="mb-4 flex items-center gap-2"><KioImg src={KIO.target} /><h3 className="text-sm font-semibold">Top agentes SLA</h3></div><div className="space-y-2">{topAgents.map((agent, idx) => <div key={`${agent.name}-${idx}`} className="flex items-center justify-between rounded-xl border border-emerald-500/20 bg-emerald-500/10 px-3 py-2 text-sm"><span>{agent.name}</span><span>{fmtPct(agent.compliance)} | {fmtMin(agent.avgAssignment)}</span></div>)}{!topAgents.length && <p className="text-sm text-violet-200/70">Sin agentes destacados</p>}</div></div>
        <div className={sectionCard}><div className="mb-4 flex items-center gap-2"><KioImg src={KIO.warning} /><h3 className="text-sm font-semibold">Agentes en riesgo</h3></div><div className="space-y-2">{riskAgents.map((agent, idx) => <div key={`${agent.name}-${idx}`} className="flex items-center justify-between rounded-xl border border-amber-500/20 bg-amber-500/10 px-3 py-2 text-sm"><span>{agent.name}</span><span>Overdue {agent.overdue} | Carga {agent.load.toFixed(0)}%</span></div>)}{!riskAgents.length && <p className="text-sm text-violet-200/70">Sin agentes en riesgo</p>}</div></div>
      </section>

      <section className={sectionCard}>
        <div className="mb-4 flex flex-wrap items-center gap-2">
          {TABS.map((tab) => { const active = activeTab === tab.key; return <button key={tab.key} onClick={() => { setActiveTab(tab.key); setTicketPage(1); setAssignmentPage(1); }} className={`inline-flex items-center gap-2 rounded-xl border px-3 py-2 text-sm transition-all ${active ? 'border-violet-300/60 bg-violet-500/30 text-violet-100 shadow-[0_0_18px_rgba(168,85,247,0.25)]' : 'border-violet-500/20 bg-black/20 text-violet-200/80 hover:border-violet-400/50 hover:bg-violet-500/15'}`}><KioImg src={tab.icon} className="h-4 w-4" style={{ filter: active ? 'brightness(0) invert(1)' : 'brightness(0) saturate(100%) invert(80%) sepia(20%) saturate(400%) hue-rotate(220deg)' }} />{tab.label}</button>; })}
        </div>

        {activeTab === 'tickets' && (
          <div>
            <div className="mb-4 flex flex-wrap items-center gap-2"><div className="relative"><Search className="pointer-events-none absolute left-2 top-2.5 h-4 w-4 text-violet-200/70" /><input value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} placeholder="Buscar ticket / asunto / cuenta" className="rounded-xl border border-violet-500/25 bg-black/20 py-2 pl-8 pr-3 text-sm" /></div><select value={sortBy} onChange={(e) => setSortBy(e.target.value)} className="rounded-xl border border-violet-500/25 bg-black/20 px-3 py-2 text-sm"><option value="ticket_id" className="bg-[#1b0f38]">Ticket ID</option><option value="priority" className="bg-[#1b0f38]">Prioridad</option><option value="status" className="bg-[#1b0f38]">Estado</option><option value="sla" className="bg-[#1b0f38]">SLA</option><option value="age_minutes" className="bg-[#1b0f38]">Edad</option></select><select value={sortOrder} onChange={(e) => setSortOrder(e.target.value)} className="rounded-xl border border-violet-500/25 bg-black/20 px-3 py-2 text-sm"><option value="desc" className="bg-[#1b0f38]">Desc</option><option value="asc" className="bg-[#1b0f38]">Asc</option></select></div>
            {loadingTab ? <Skeleton className="h-64" /> : <div className="overflow-x-auto rounded-xl border border-violet-500/15"><table className="min-w-[1100px] w-full text-left text-xs"><thead className="bg-black/20 text-violet-200/80"><tr><th className="px-2 py-2">Ticket ID</th><th className="px-2 py-2">Asunto</th><th className="px-2 py-2">Cuenta</th><th className="px-2 py-2">Prioridad</th><th className="px-2 py-2">Estado</th><th className="px-2 py-2">Tecnico</th><th className="px-2 py-2">SLA</th><th className="px-2 py-2">Edad</th><th className="px-2 py-2">Accion recomendada</th></tr></thead><tbody>{ticketsData.items.map((row, idx) => { const id = row.ticket_id || row.id || row.ticket || `ticket-${idx}`; return <tr key={id} className="cursor-pointer border-t border-violet-500/10 hover:bg-violet-500/10" onClick={() => openTicketDrawer(id)}><td className="px-2 py-2 font-semibold">#{id}</td><td className="px-2 py-2">{row.asunto || row.subject || 'N/D'}</td><td className="px-2 py-2">{row.cuenta || row.account || 'N/D'}</td><td className="px-2 py-2"><StatusBadge value={row.prioridad || row.priority} /></td><td className="px-2 py-2"><StatusBadge value={row.estado || row.status} /></td><td className="px-2 py-2">{row.tecnico || row.technician || 'Sin asignar'}</td><td className="px-2 py-2"><StatusBadge value={row.sla_status || row.sla_state || row.sla || 'N/D'} /></td><td className="px-2 py-2">{getNumber(row.edad_horas ?? row.age_minutes)} h</td><td className="px-2 py-2">{row.accion_recomendada || row.recommended_action || '-'}</td></tr>; })}{!ticketsData.items.length && <tr><td colSpan="9" className="px-2 py-6 text-center text-violet-200/70">Sin tickets para los filtros actuales</td></tr>}</tbody></table></div>}
            <div className="mt-3 flex items-center justify-end gap-2 text-sm"><button onClick={() => setTicketPage((prev) => Math.max(1, prev - 1))} disabled={ticketPage <= 1} className="rounded-lg border border-violet-500/30 bg-black/20 p-1 disabled:opacity-50"><ChevronLeft className="h-4 w-4" /></button><span>Pagina {ticketPage} / {ticketPages}</span><button onClick={() => setTicketPage((prev) => Math.min(ticketPages, prev + 1))} disabled={ticketPage >= ticketPages} className="rounded-lg border border-violet-500/30 bg-black/20 p-1 disabled:opacity-50"><ChevronRight className="h-4 w-4" /></button></div>
          </div>
        )}

        {activeTab === 'unassigned' && (
          <div>{loadingTab ? <Skeleton className="h-64" /> : <div className="overflow-x-auto rounded-xl border border-violet-500/15"><table className="min-w-[980px] w-full text-left text-xs"><thead className="bg-black/20 text-violet-200/80"><tr><th className="px-2 py-2">Ticket</th><th className="px-2 py-2">SLA riesgo</th><th className="px-2 py-2">Antiguedad</th><th className="px-2 py-2">Prioridad</th><th className="px-2 py-2">Skill requerido</th><th className="px-2 py-2">Acciones</th></tr></thead><tbody>{unassignedData.map((row, idx) => { const id = row.ticket_id || row.id || `u-${idx}`; return <tr key={id} className="border-t border-violet-500/10"><td className="px-2 py-2 font-semibold">#{id}</td><td className="px-2 py-2"><StatusBadge value={row.sla_status || row.sla_state || row.sla_risk || 'N/D'} /></td><td className="px-2 py-2">{getNumber(row.edad_horas ?? row.age_minutes)} h</td><td className="px-2 py-2"><StatusBadge value={row.prioridad || row.priority} /></td><td className="px-2 py-2">{row.required_skill || row.skill || row.producto || 'N/D'}</td><td className="px-2 py-2"><div className="flex gap-2"><button className="rounded-lg border border-violet-400/35 bg-violet-500/20 px-2 py-1 text-xs">Asignar</button><button className="rounded-lg border border-cyan-400/35 bg-cyan-500/20 px-2 py-1 text-xs">Ver candidatos</button></div></td></tr>; })}{!unassignedData.length && <tr><td colSpan="6" className="px-2 py-6 text-center text-violet-200/70">No hay tickets sin asignar</td></tr>}</tbody></table></div>}</div>
        )}

        {activeTab === 'assignments' && (
          <div><div className="mb-3"><select value={assignmentTypeFilter} onChange={(e) => setAssignmentTypeFilter(e.target.value)} className="rounded-xl border border-violet-500/25 bg-black/20 px-3 py-2 text-sm"><option value="" className="bg-[#1b0f38]">Todos</option><option value="AUTO" className="bg-[#1b0f38]">AUTO</option><option value="MANUAL" className="bg-[#1b0f38]">MANUAL</option><option value="OVERRIDE" className="bg-[#1b0f38]">OVERRIDE</option></select></div>{loadingTab ? <Skeleton className="h-64" /> : <div className="overflow-x-auto rounded-xl border border-violet-500/15"><table className="min-w-[980px] w-full text-left text-xs"><thead className="bg-black/20 text-violet-200/80"><tr><th className="px-2 py-2">Ticket</th><th className="px-2 py-2">Tecnico asignado</th><th className="px-2 py-2">Tipo</th><th className="px-2 py-2">Score</th><th className="px-2 py-2">Motivo</th><th className="px-2 py-2">Fecha (CDMX)</th><th className="px-2 py-2">Override</th></tr></thead><tbody>{assignmentsData.items.map((row, idx) => { const id = row.ticket_id || row.ticket || `a-${idx}`; return <tr key={id} className="cursor-pointer border-t border-violet-500/10 hover:bg-violet-500/10" onClick={() => openAssignmentModal(id)}><td className="px-2 py-2 font-semibold">#{id}</td><td className="px-2 py-2">{row.assigned_agent_name || row.tecnico || row.technician || row.assigned_agent_id || 'N/D'}</td><td className="px-2 py-2"><StatusBadge value={row.assignment_type || row.type || row.tipo} /></td><td className="px-2 py-2">{getNumber(row.score).toFixed(2)}</td><td className="px-2 py-2">{row.reason || row.motivo || '-'}</td><td className="px-2 py-2">{formatMexicoDateTime(row.assigned_at || row.date || row.fecha)}</td><td className="px-2 py-2">{String(row.override_by || row.override || row.is_override || false)}</td></tr>; })}{!assignmentsData.items.length && <tr><td colSpan="7" className="px-2 py-6 text-center text-violet-200/70">Sin historial de asignaciones</td></tr>}</tbody></table></div>}<div className="mt-3 flex items-center justify-end gap-2 text-sm"><button onClick={() => setAssignmentPage((prev) => Math.max(1, prev - 1))} disabled={assignmentPage <= 1} className="rounded-lg border border-violet-500/30 bg-black/20 p-1 disabled:opacity-50"><ChevronLeft className="h-4 w-4" /></button><span>Pagina {assignmentPage} / {assignmentPages}</span><button onClick={() => setAssignmentPage((prev) => Math.min(assignmentPages, prev + 1))} disabled={assignmentPage >= assignmentPages} className="rounded-lg border border-violet-500/30 bg-black/20 p-1 disabled:opacity-50"><ChevronRight className="h-4 w-4" /></button></div></div>
        )}

        {activeTab === 'agents' && (
          <div>{loadingTab ? <Skeleton className="h-64" /> : <div className="overflow-x-auto rounded-xl border border-violet-500/15"><table className="min-w-[980px] w-full text-left text-xs"><thead className="bg-black/20 text-violet-200/80"><tr><th className="px-2 py-2">Agente</th><th className="px-2 py-2">Disponibilidad</th><th className="px-2 py-2">Shift</th><th className="px-2 py-2">Carga</th><th className="px-2 py-2">SLA</th><th className="px-2 py-2">Skills</th><th className="px-2 py-2">Tiempo en estado</th></tr></thead><tbody>{agentsData.map((row, idx) => <tr key={`${row.tecnico || row.agent || idx}`} className="border-t border-violet-500/10"><td className="px-2 py-2 font-semibold">{row.tecnico || row.agent || `Agente ${idx + 1}`}</td><td className="px-2 py-2"><StatusBadge value={row.status_actual || row.availability || row.disponibilidad} /></td><td className="px-2 py-2">{(row.is_in_shift ? 'En turno' : 'Fuera de turno')}</td><td className="px-2 py-2">{getNumber(row.load_percentage ?? row.load_percent).toFixed(0)}%</td><td className="px-2 py-2"><StatusBadge value={row.tickets_sla_vencido > 0 ? 'VENCIDO' : row.tickets_riesgo_sla > 0 ? 'RIESGO' : 'OK'} /></td><td className="px-2 py-2">{Array.isArray(row.skills) ? row.skills.join(', ') : (row.skills || '-')}</td><td className="px-2 py-2">{(row.minutes_in_status !== undefined && row.minutes_in_status !== null ? String(row.minutes_in_status) + ' min' : '-')}</td></tr>)}{!agentsData.length && <tr><td colSpan="7" className="px-2 py-6 text-center text-violet-200/70">Sin datos realtime de agentes</td></tr>}</tbody></table></div>}</div>
        )}
      </section>

      {selectedTicketId && (
        <div className="fixed inset-y-0 right-0 z-[70] w-full max-w-xl border-l border-violet-500/35 bg-[#120823]/95 p-5 backdrop-blur-xl">
          <div className="mb-4 flex items-center justify-between"><h3 className="text-lg font-semibold">Ticket #{selectedTicketId}</h3><button onClick={() => setSelectedTicketId(null)} className="rounded-lg border border-violet-400/40 p-1"><X className="h-4 w-4" /></button></div>
          {ticketDetailLoading ? <Skeleton className="h-56" /> : ticketDetail ? <div className="space-y-3 text-sm"><div className="rounded-xl border border-violet-500/20 bg-black/20 p-3"><p className="font-semibold">Detalle ticket (CDMX)</p><pre className="mt-2 max-h-28 overflow-auto whitespace-pre-wrap text-xs text-violet-200/80">{JSON.stringify(ticketDetail.detail || ticketDetail, mexicoDateJsonReplacer, 2)}</pre></div><div className="rounded-xl border border-violet-500/20 bg-black/20 p-3"><p className="font-semibold">Timeline</p><pre className="mt-2 max-h-28 overflow-auto whitespace-pre-wrap text-xs text-violet-200/80">{JSON.stringify(ticketDetail.timeline || [], mexicoDateJsonReplacer, 2)}</pre></div><div className="rounded-xl border border-violet-500/20 bg-black/20 p-3"><p className="font-semibold">SLA Alerts</p><pre className="mt-2 max-h-28 overflow-auto whitespace-pre-wrap text-xs text-violet-200/80">{JSON.stringify(ticketDetail.alertas_sla || ticketDetail.sla_alerts || [], mexicoDateJsonReplacer, 2)}</pre></div><div className="rounded-xl border border-violet-500/20 bg-black/20 p-3"><p className="font-semibold">Metadata</p><pre className="mt-2 max-h-28 overflow-auto whitespace-pre-wrap text-xs text-violet-200/80">{JSON.stringify(ticketDetail.metadata || {}, mexicoDateJsonReplacer, 2)}</pre></div></div> : <EmptyState title="Sin detalle" description="No fue posible obtener el detalle del ticket." />}
        </div>
      )}

      {selectedAssignmentTicketId && (
        <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/50 p-4">
          <div className="max-h-[90vh] w-full max-w-3xl overflow-auto rounded-2xl border border-violet-500/35 bg-[#120823]/95 p-5 backdrop-blur-xl">
            <div className="mb-4 flex items-center justify-between"><h3 className="flex items-center gap-2 text-lg font-semibold"><KioImg src={KIO.brain} className="h-5 w-5" />AI Decision Engine - Ticket #{selectedAssignmentTicketId}</h3><button onClick={() => setSelectedAssignmentTicketId(null)} className="rounded-lg border border-violet-400/40 p-1"><X className="h-4 w-4" /></button></div>
            {assignmentDetailLoading ? <Skeleton className="h-56" /> : assignmentDetail ? <div className="grid grid-cols-1 gap-3 md:grid-cols-2"><div className="rounded-xl border border-violet-500/20 bg-black/20 p-3"><p className="mb-2 text-sm font-semibold">Score final</p><p className="text-2xl font-bold">{getNumber(assignmentDetail.final_score ?? assignmentDetail.score).toFixed(2)}</p></div><div className="rounded-xl border border-violet-500/20 bg-black/20 p-3"><p className="mb-2 text-sm font-semibold">Timeline (CDMX)</p><pre className="max-h-24 overflow-auto whitespace-pre-wrap text-xs text-violet-200/80">{JSON.stringify(assignmentDetail.timeline || [], mexicoDateJsonReplacer, 2)}</pre></div><div className="rounded-xl border border-violet-500/20 bg-black/20 p-3"><p className="mb-2 text-sm font-semibold">Reglas duras</p><pre className="max-h-24 overflow-auto whitespace-pre-wrap text-xs text-violet-200/80">{JSON.stringify(assignmentDetail.reglas_duras || assignmentDetail.hard_rules || [], mexicoDateJsonReplacer, 2)}</pre></div><div className="rounded-xl border border-violet-500/20 bg-black/20 p-3"><p className="mb-2 text-sm font-semibold">Reglas blandas</p><pre className="max-h-24 overflow-auto whitespace-pre-wrap text-xs text-violet-200/80">{JSON.stringify(assignmentDetail.reglas_blandas || assignmentDetail.soft_rules || [], mexicoDateJsonReplacer, 2)}</pre></div><div className="rounded-xl border border-violet-500/20 bg-black/20 p-3"><p className="mb-2 text-sm font-semibold">Candidatos evaluados</p><pre className="max-h-24 overflow-auto whitespace-pre-wrap text-xs text-violet-200/80">{JSON.stringify(assignmentDetail.candidatos_evaluados || assignmentDetail.candidates_evaluated || [], mexicoDateJsonReplacer, 2)}</pre></div><div className="rounded-xl border border-violet-500/20 bg-black/20 p-3"><p className="mb-2 text-sm font-semibold">Candidatos descartados</p><pre className="max-h-24 overflow-auto whitespace-pre-wrap text-xs text-violet-200/80">{JSON.stringify(assignmentDetail.candidatos_rechazados || assignmentDetail.candidates_discarded || [], mexicoDateJsonReplacer, 2)}</pre></div></div> : <EmptyState title="Sin detalle de asignacion" description="No se pudo recuperar la trazabilidad de decision." />}
          </div>
        </div>
      )}
    </div>
  );
}
